import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { sharedLib } from "@shared/services/shared.service.ts";
import { useGetUserQuery } from "@shared/utils/hooks/misc.ts";
import { useBranding } from "@shared/utils/hooks/useBranding.ts";

export const DEFAULT_TITLE = "Cornix";
export const DEFAULT_DESCRIPTION = "Cornix";
export const DEFAULT_FAVICON = "/favicon.ico";
export const DEFAULT_MANIFEST = "/manifest.json";

export const getDynamicManifest = (
  tabTitle?: string,
  favicon?: string
): string | undefined => {
  if (!tabTitle) return undefined;

  const dynamicManifest = {
    short_name: tabTitle,
    name: tabTitle,
    icons: favicon
      ? [
          {
            src: favicon,
            sizes: "64x64 32x32 24x24 16x16",
            type: "image/x-icon",
          },
          {
            src: favicon,
            type: "image/png",
            sizes: "192x192",
          },
          {
            src: favicon,
            type: "image/png",
            sizes: "512x512",
          },
        ]
      : [],
    start_url: "/",
    display: "standalone",
    theme_color: "#000000",
    background_color: "#ffffff",
  };

  const stringManifest = JSON.stringify(dynamicManifest);
  const base64Manifest = btoa(stringManifest);
  return `data:application/json;base64,${base64Manifest}`;
};

export const setFaviconHref = (href: string | undefined) => {
  if (!href) return;
  const link = document.getElementById("app-favicon") as HTMLLinkElement | null;
  if (!link) return;
  link.setAttribute("href", href);
  if (href.startsWith("data:image/")) {
    const type = href.slice(5, href.indexOf(";"));
    if (type) link.setAttribute("type", type);
  } else if (href.endsWith(".ico")) link.setAttribute("type", "image/x-icon");
  else if (href.endsWith(".png")) link.setAttribute("type", "image/png");
  else if (href.endsWith(".svg")) link.setAttribute("type", "image/svg+xml");
  else link.removeAttribute("type");
};

export const makeCircularFavicon = (
  src?: string
): Promise<string | undefined> => {
  return new Promise((resolve) => {
    if (!src) return resolve(undefined);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const w = img.naturalWidth || img.width;
        const h = img.naturalHeight || img.height;
        const size = Math.min(w, h);
        if (!size || size === Infinity) return resolve(undefined);
        const sx = (w - size) / 2;
        const sy = (h - size) / 2;
        const target = 64;
        const canvas = document.createElement("canvas");
        canvas.width = target;
        canvas.height = target;
        const ctx = canvas.getContext("2d");
        if (!ctx) return resolve(undefined);
        ctx.clearRect(0, 0, target, target);
        ctx.beginPath();
        ctx.arc(target / 2, target / 2, target / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = "high";
        ctx.drawImage(img, sx, sy, size, size, 0, 0, target, target);
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(undefined);
      }
    };
    img.onerror = () => resolve(undefined);
    img.src = src;
  });
};

export const useBaseBranding = (params: {
  title?: string;
  description?: string;
  favicon?: string;
  manifest?: string;
  isLoading?: boolean;
}) => {
  const { title, description, favicon, manifest, isLoading } = params;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    const effectiveTitle = title || DEFAULT_TITLE;
    const effectiveDescription = description || DEFAULT_DESCRIPTION;
    const effectiveFavicon = favicon || DEFAULT_FAVICON;
    const effectiveManifest = manifest || DEFAULT_MANIFEST;

    let currentManifestUrl: string | undefined;

    const applyBranding = async () => {
      if (document.title !== effectiveTitle) {
        document.title = effectiveTitle;
      }

      let meta = document.querySelector('meta[name="description"]');
      if (!meta) {
        meta = document.createElement("meta");
        meta.setAttribute("name", "description");
        document.head.appendChild(meta);
      }
      if (meta.getAttribute("content") !== effectiveDescription) {
        meta.setAttribute("content", effectiveDescription);
      }

      const manifestLink = document.getElementById(
        "app-manifest"
      ) as HTMLLinkElement | null;

      let manifestToApply = effectiveManifest;
      if (effectiveManifest?.startsWith("data:application/json")) {
        try {
          const base64Data = effectiveManifest.split(",")[1];
          const jsonString = atob(base64Data);
          const blob = new Blob([jsonString], {
            type: "application/manifest+json",
          });
          currentManifestUrl = URL.createObjectURL(blob);
          manifestToApply = currentManifestUrl;
        } catch {}
      }

      if (manifestToApply) {
        if (manifestLink) {
          if (manifestLink.getAttribute("href") !== manifestToApply) {
            manifestLink.setAttribute("href", manifestToApply);
          }
        } else {
          const newManifestLink = document.createElement("link");
          newManifestLink.setAttribute("id", "app-manifest");
          newManifestLink.setAttribute("rel", "manifest");
          newManifestLink.setAttribute("href", manifestToApply);
          document.head.appendChild(newManifestLink);
        }
      } else if (manifestLink) {
        if (manifestLink.getAttribute("href") !== DEFAULT_MANIFEST) {
          manifestLink.setAttribute("href", DEFAULT_MANIFEST);
        }
      }

      const appleIcon = document.getElementById(
        "apple-touch-icon"
      ) as HTMLLinkElement | null;
      if (appleIcon) {
        if (appleIcon.getAttribute("href") !== effectiveFavicon) {
          appleIcon.setAttribute("href", effectiveFavicon);
        }
      } else {
        const newAppleIcon = document.createElement("link");
        newAppleIcon.setAttribute("id", "apple-touch-icon");
        newAppleIcon.setAttribute("rel", "apple-touch-icon");
        newAppleIcon.setAttribute("href", effectiveFavicon);
        document.head.appendChild(newAppleIcon);
      }

      if (effectiveFavicon) {
        setFaviconHref(effectiveFavicon);
        const circular = await makeCircularFavicon(effectiveFavicon);
        if (circular) setFaviconHref(circular);
      } else {
        setFaviconHref(DEFAULT_FAVICON);
      }
    };

    applyBranding().catch(() => {});

    return () => {
      if (currentManifestUrl) {
        URL.revokeObjectURL(currentManifestUrl);
      }
    };
  }, [title, description, favicon, manifest, isLoading]);
};

export const useAppBranding = () => {
  const getRealUserQuery = useGetUserQuery(false);
  const user = useSelector(sharedLib.selectors.authentication.selectRealUser);
  const { brandingData, getBrandingDataStatus } = useBranding();

  const isWhitelabelEnabled = brandingData?.whitelabel;
  const hasBranding = !user || isWhitelabelEnabled;
  const currentConfig = hasBranding ? brandingData : undefined;

  const title = currentConfig?.tab_title;
  const description = currentConfig?.tab_title;
  const favicon = currentConfig?.favicon;

  const manifest = useMemo(() => {
    if (currentConfig) {
      return getDynamicManifest(
        currentConfig?.tab_title,
        currentConfig?.favicon
      );
    }
  }, [currentConfig]);

  useBaseBranding({
    title,
    description,
    favicon,
    manifest,
    isLoading: getBrandingDataStatus.isLoading || getRealUserQuery.isLoading,
  });
};
